import type { CSSProperties } from "react";
import { useCarousel } from "../../hooks";
import type { Highlight } from "../../types";
import "./BannerCarousel.css";
import { assetUrl, siteUrl } from "../../wordpress";

type BannerCarouselProps = {
	highlights: Highlight[];
	autoPlayMs?: number;
};

/**
 * Carrossel de destaques: um banner por vez, painel de cor à esquerda e
 * fotografia à direita. Avança sozinho, pára ao passar o rato e aceita arrasto.
 * Toda a mecânica vive em `useCarousel`; aqui só há apresentação.
 */
export function BannerCarousel({
	highlights,
	autoPlayMs = 6000,
}: BannerCarouselProps) {
	const carousel = useCarousel({ count: highlights.length, autoPlayMs });

	if (highlights.length === 0) return null;

	return (
		<section
			className="banner-carousel"
			aria-roledescription="carrusel"
			aria-label="Destacats"
			onMouseEnter={carousel.pause}
			onMouseLeave={carousel.resume}
		>
			<div className="banner-carousel__viewport" {...carousel.dragHandlers}>
				<div
					className="banner-carousel__track"
					style={{
						transform: `translate3d(calc(${-carousel.index * 100}% + ${carousel.dragOffset}px), 0, 0)`,
						transition: carousel.isDragging
							? "none"
							: "transform 600ms cubic-bezier(.22,.61,.36,1)",
					}}
				>
					{highlights.map((highlight, position) => (
						<BannerSlide
							key={highlight.id}
							highlight={highlight}
							hidden={position !== carousel.index}
							position={position + 1}
							total={highlights.length}
						/>
					))}
				</div>
			</div>

			<button
				type="button"
				className="banner-carousel__arrow banner-carousel__arrow--prev"
				onClick={carousel.previous}
			>
				<span aria-hidden="true">‹</span>
				<span className="visually-hidden">Anterior</span>
			</button>
			<button
				type="button"
				className="banner-carousel__arrow banner-carousel__arrow--next"
				onClick={carousel.next}
			>
				<span aria-hidden="true">›</span>
				<span className="visually-hidden">Següent</span>
			</button>

		</section>
	);
}

type BannerSlideProps = {
	highlight: Highlight;
	hidden: boolean;
	position: number;
	total: number;
};

function BannerSlide({ highlight, hidden, position, total }: BannerSlideProps) {
	return (
		<article
			className="banner"
			aria-hidden={hidden}
			aria-roledescription="diapositiva"
			aria-label={`${position} de ${total}`}
		>
			<a
				className="banner__link"
				href={siteUrl(highlight.href)}
				tabIndex={hidden ? -1 : 0}
				style={{ "--slide-accent": highlight.color } as CSSProperties}
			>
				<div
					className={
						highlight.inkOnColor
							? "banner__panel banner__panel--ink"
							: "banner__panel"
					}
				>
					<p className="banner__kicker">{highlight.kicker}</p>
					<h2 className="banner__title">{highlight.title}</h2>
					<p className="banner__description">{highlight.description}</p>
				</div>
				<figure className="banner__figure">
					<img
						src={assetUrl(highlight.image)}
						alt={highlight.title}
						draggable={false}
						loading={position > 1 ? "lazy" : undefined}
					/>
				</figure>
			</a>
		</article>
	);
}
